import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { PLAN_FORM_CSS as styles } from "../manage-plan-new/planFormTheme";
import {
  useSegments, useTiers, useAllPlans, useFeatureMaster, usePlanFeatures,
  useCreateTier, useUpdateTier, useUpdateSegment,
} from "../hooks/useRateCard";
import { stepsForBand, marksText } from "./taper";

/**
 * Add or edit one pricing band.
 *
 * A tier is priced one of two ways and never both: a flat monthly fee, or a
 * per-trademark rate with a floor beneath it. Which one is on offer follows the
 * segment — corporate bands are flat, attorney-style bands are per mark — so the
 * form asks for one set of numbers, not both, and sends the other side as null.
 *
 * Features work the way they do on a plan: pick them from the same cards. Link a
 * plan and the tier inherits that plan's list instead, which is why the cards go
 * read-only rather than disappearing — an admin should see what will print.
 */

const EMPTY = {
  name: "",
  min_marks: "",
  max_marks: "",
  included_users: "",
  free_clients: "",
  price_monthly: "",
  per_mark_rate_yr: "",
  floor_monthly: "",
  onboarding_charge: "",
  extra_user_cost: "",
  extra_client_cost: "",
  quarterly_discount_pct: "",
  annual_discount_pct: "",
  max_additional_discount_pct: "",
  max_onboarding_discount_pct: "",
  gst_percent: "",
  overage_threshold_marks: "",
  overage_per_mark_yr: "",
  plan_id: "",
  sort_order: "",
  is_active: true,
};

/**
 * The tier's own feature ids, however the row carries them — the column is a CSV
 * string, but an array is just as plausible from an API that parsed it.
 */
const parseIds = (v) =>
  (Array.isArray(v) ? v : String(v ?? "").split(","))
    .map((x) => Number(String(x).trim()))
    .filter((n) => Number.isFinite(n) && n > 0);

/** Blank stays blank: an empty box means "not set", which is not the same as 0. */
const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const toText = (v) => (v === null || v === undefined ? "" : String(v));

/**
 * One numeric input.
 *
 * Declared out here, not inside the page: a component defined during render is a
 * new type every keystroke, and React remounts it — which is how a field loses
 * focus after every character typed.
 */
const Field = ({ name, label, placeholder, suffix, note, type = "number", required, form, set, errors }) => (
  <div className="asp-field">
    <label className="asp-label" htmlFor={name}>
      {label}{required && <span className="asp-req">*</span>}
    </label>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        id={name} name={name} type={type} className="form-control"
        value={form[name]} placeholder={placeholder}
        onChange={(e) => set({ [name]: e.target.value })}
        style={{ width: "100%" }}
      />
      {suffix && (
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#8b92a9", whiteSpace: "nowrap" }}>
          {suffix}
        </span>
      )}
    </div>
    {note && <div style={{ fontSize: 12, color: "#8b92a9", marginTop: 6, lineHeight: 1.5 }}>{note}</div>}
    {errors[name] && <div className="asp-error" style={{ marginTop: 8 }}>{errors[name]}</div>}
  </div>
);

const AddEditTier = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  /* The row travels with the click, so an edit opens filled in without a second
     round trip. Arriving cold — a refresh, a pasted URL — it is found in the
     segment's list instead. */
  const passed = location.state?.tier || null;
  const segment = params.get("segment") || passed?.segment || "corporate";
  /* Reading a band is the common case — checking what a tier includes should not
     mean opening it for editing and remembering not to save. */
  const isView = params.get("view") === "1";

  const { data: segments = [] } = useSegments();
  const { data: tiers = [] } = useTiers(isEdit && !passed ? segment : null);
  const { data: plans = [] } = useAllPlans({ includeProposalOnly: true });
  const { data: masterFeatures = [], isLoading: featureLoading } = useFeatureMaster();

  const createTier = useCreateTier();
  const updateTier = useUpdateTier();
  const saveSegment = useUpdateSegment();
  const submitting = createTier.isPending || updateTier.isPending || saveSegment.isPending;

  const [form, setForm] = useState(EMPTY);
  const [features, setFeatures] = useState([]);
  const [errors, setErrors] = useState({});
  const [loaded, setLoaded] = useState(!isEdit);

  const existing = passed || tiers.find((t) => String(t.id) === String(id));
  const currentSegment = segments.find((s) => s.slug === segment);
  /* Corporate is `is_attorney: 0` and priced flat; everything above it — attorney
     and any segment added later — is priced per trademark. */
  const perMark = Boolean(currentSegment && Number(currentSegment.is_attorney) !== 0);

  useEffect(() => {
    if (!isEdit || !existing || loaded) return;
    setForm({
      ...EMPTY,
      ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, toText(existing[k])])),
      is_active: existing.is_active !== false,
    });
    /* The row's own list is the source of truth when it has one: it is what the
       rep saved, and it has diverged from the plan. Marked as touched too, so
       saving without changing anything cannot send null and hand the tier back
       to its plan. Only an empty column falls through to the API below. */
    const savedIds = parseIds(existing.features);
    if (savedIds.length) {
      setFeatures(savedIds);
      setFeaturesTouched(true);
    }
    setLoaded(true);
  }, [isEdit, existing, loaded]);

  /* A linked plan's features, or — while editing — the ones this tier already
     resolves to, whichever way it got them. */
  const planId = toNum(form.plan_id);
  /* Both ids on an edit: the API returns the tier's own features when it has
     them and the plan's when it does not, so this is the one call that answers
     "what will these customers actually get". */
  const { data: resolved } = usePlanFeatures({
    planId,
    rateCardId: isEdit ? Number(id) : null,
    segment,
  });
  const resolvedIds = useMemo(() => resolved?.featureIds || [], [resolved]);

  /* The fallback: a tier with no list of its own starts from its linked plan's.
     Skipped once anything is chosen — or saved — so a refetch cannot undo it. */
  const [featuresTouched, setFeaturesTouched] = useState(false);
  useEffect(() => {
    if (featuresTouched || !resolvedIds.length) return;
    setFeatures(resolvedIds);
  }, [resolvedIds, featuresTouched]);

  /* A linked plan sets the subscription identity and seeds the feature list —
     it does not own it. A tier may include more or fewer than its plan, and the
     customer gets what the tier says. */
  const linkedPlan = Boolean(planId);

  /* The taper steps this band sits across — one when it fits inside a step,
     two when it straddles. Editable here because an admin setting up a band
     should not have to go hunting for its price on another screen. */
  const taper = currentSegment?.taper;
  const bandSteps = perMark ? stepsForBand(taper, form.min_marks, form.max_marks) : [];
  const [rateEdits, setRateEdits] = useState({});

  const set = (patch) => { setForm((prev) => ({ ...prev, ...patch })); setErrors({}); };

  const toggleFeature = (featureId) => {
    setFeaturesTouched(true);
    setFeatures((prev) =>
      prev.includes(featureId) ? prev.filter((x) => x !== featureId) : [...prev, featureId],
    );
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "A tier needs a name — it is what the rep picks";
    if (!perMark && form.price_monthly === "") next.price_monthly = "A flat band needs a monthly price";
    if (!linkedPlan && features.length === 0) next.features = "Pick what this tier includes, or link a plan";
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const found = validate();
    if (Object.keys(found).length) { setErrors(found); return; }

    const body = {
      segment,
      name: form.name.trim(),
      min_marks: toNum(form.min_marks),
      max_marks: toNum(form.max_marks),
      included_users: toNum(form.included_users),
      free_clients: toNum(form.free_clients),
      // The unused shape is cleared, so a band can never carry two prices.
      price_monthly: perMark ? null : toNum(form.price_monthly),
      // Carried through untouched on a per-mark band; cleared on a flat one.
      per_mark_rate_yr: perMark ? toNum(form.per_mark_rate_yr) : null,
      floor_monthly: perMark ? toNum(form.floor_monthly) : null,
      onboarding_charge: toNum(form.onboarding_charge),
      extra_user_cost: toNum(form.extra_user_cost),
      /* Zero, not null: the column will not take a null, and on an unlimited
         band "nothing extra is charged" is exactly what zero means. A blank box
         says the same thing. */
      extra_client_cost: Number(form.free_clients) === -1
        ? 0
        : (toNum(form.extra_client_cost) ?? 0),
      quarterly_discount_pct: toNum(form.quarterly_discount_pct),
      annual_discount_pct: toNum(form.annual_discount_pct),
      max_additional_discount_pct: toNum(form.max_additional_discount_pct),
      max_onboarding_discount_pct: toNum(form.max_onboarding_discount_pct),
      gst_percent: toNum(form.gst_percent),
      overage_threshold_marks: toNum(form.overage_threshold_marks),
      overage_per_mark_yr: toNum(form.overage_per_mark_yr),
      plan_id: planId,
      /* Comma-separated ids, as the column stores them — but only once someone
         has actually chosen. A tier nobody edited stays null so it keeps
         inheriting its plan live: change the plan's features and every tier that
         never diverged follows, which is the point of linking one. */
      features: featuresTouched && features.length ? features.join(",") : null,
      sort_order: toNum(form.sort_order),
      is_active: form.is_active,
    };

    /* A rate lives on the segment, so a changed one is a second save. It goes
       first: if it fails, the tier is left untouched rather than saved against a
       price that never landed. */
    const changed = Object.entries(rateEdits).filter(
      ([i, v]) => String(v) !== "" && Number(v) !== Number(taper[i].rate),
    );

    const saveTier = () => {
      const done = { onSuccess: () => navigate("/cms/rate-card") };
      if (isEdit) updateTier.mutate({ id: Number(id), ...body }, done);
      else createTier.mutate(body, done);
    };

    if (!changed.length) return saveTier();

    const nextTaper = taper.map((step, i) =>
      rateEdits[i] != null && String(rateEdits[i]) !== ""
        ? { ...step, rate: Number(rateEdits[i]) }
        : step,
    );
    saveSegment.mutate(
      { id: currentSegment.id, taper: nextTaper },
      { onSuccess: saveTier },
    );
  };

  const f = { form, set, errors };

  return (
    <>
      <style>{styles}</style>
      <div className="main-content asp-scope">
        <div className="page-content">
          <div className="container-fluid">

            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0 font-size-18">{isEdit ? "Edit Tier" : "Add Tier"}</h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">CMS</li>
                      <li className="breadcrumb-item"><Link to="/cms/rate-card">Rate Card</Link></li>
                      <li className="breadcrumb-item active">{isEdit ? "Edit Tier" : "Add Tier"}</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-12">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">

                      <div className="asp-card-header">
                        <div className="asp-card-icon">
                          <i className={`fas ${isView ? "fa-eye" : isEdit ? "fa-edit" : "fa-plus"}`} />
                        </div>
                        <div>
                          <div className="asp-card-title">
                            {isView ? (form.name || "Tier") : isEdit ? "Edit Tier" : "Add Tier"}
                          </div>
                          <div className="asp-card-subtitle">
                            {currentSegment?.label || segment} ·{" "}
                            {perMark ? "priced per trademark" : "priced as a flat monthly fee"}
                          </div>
                        </div>
                        {isView ? (
                          <span className="asp-mode-badge" style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" }}>
                            👁 View Only
                          </span>
                        ) : (
                          <span className={`asp-mode-badge ${isEdit ? "asp-mode-badge-edit" : "asp-mode-badge-create"}`}>
                            {isEdit ? "✏ Edit Mode" : "✦ Create Mode"}
                          </span>
                        )}
                      </div>

                      <form onSubmit={handleSubmit}>
                        {/* One switch for the whole form — a native disabled
                            fieldset turns off every input, select, checkbox and
                            upload inside it, so nothing can be edited by a path
                            that was missed. */}
                        <fieldset
                          disabled={isView}
                          style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
                        >
                        <div className="asp-form-body">

                          {/* ── Pricing ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Pricing</div>

                            <div className="asp-field">
                              <label className="asp-label" htmlFor="name">
                                Tier name<span className="asp-req">*</span>
                              </label>
                              <input
                                id="name" name="name" type="text" className="form-control"
                                value={form.name} placeholder="Business"
                                onChange={(e) => set({ name: e.target.value })}
                              />
                              <div style={{ fontSize: 12, color: "#8b92a9", marginTop: 6 }}>
                                This is what a rep sees in the Package dropdown.
                              </div>
                              {errors.name && <div className="asp-error" style={{ marginTop: 8 }}>{errors.name}</div>}
                            </div>

                            <div className="asp-price-grid">
                              {perMark ? (
                                <>
                                  {/* The rate belongs to the segment's taper, not to
                                      this band — but it is what this band charges,
                                      so it is edited here too. */}
                                  <div className="asp-field">
                                    <label className="asp-label">Per-trademark rate</label>
                                    {bandSteps.length === 0 ? (
                                      <div style={{ fontSize: 12.5, color: "#8b92a9", lineHeight: 1.6, padding: "10px 0" }}>
                                        Set the band above, then its rate appears here.
                                      </div>
                                    ) : (
                                      <div style={{ display: "grid", gap: 10 }}>
                                        {bandSteps.map((step) => (
                                          <div key={step.index} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <span style={{ fontSize: 12.5, color: "#8b92a9", minWidth: 108 }}>
                                              {marksText(step.up_to)} marks
                                            </span>
                                            <input
                                              type="number" min="0" step="0.01" className="form-control"
                                              value={rateEdits[step.index] ?? step.rate ?? ""}
                                              onChange={(e) => {
                                                setRateEdits((prev) => ({ ...prev, [step.index]: e.target.value }));
                                                setErrors({});
                                              }}
                                              style={{ width: "100%" }}
                                            />
                                            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#8b92a9", whiteSpace: "nowrap" }}>
                                              ₹/mark/yr
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <div style={{ fontSize: 12, color: "#8b92a9", marginTop: 8, lineHeight: 1.5 }}>
                                      {bandSteps.length > 1
                                        ? "This band spans two rate steps — a quote uses whichever one the trademark count falls in."
                                        : "A quote takes its rate from the segment's taper, by trademark count."}
                                      {" "}These steps are shared: changing one re-prices every band that
                                      overlaps it.
                                    </div>
                                  </div>
                                  <Field {...f}
                                    name="floor_monthly" label="Minimum / month" suffix="₹/mo" placeholder="2399"
                                    note="Only the entry band needs one — it is what a very small book still pays. Leave blank on the higher bands."
                                  />
                                </>
                              ) : (
                                <Field {...f} name="price_monthly" label="Monthly price (flat)" suffix="₹/mo" placeholder="11999" required />
                              )}
                            </div>

                            <div className="asp-price-grid">
                              <Field {...f} name="min_marks" label="Min trademarks" placeholder="101" />
                              <Field {...f}
                                name="max_marks" label="Max trademarks" placeholder="1000"
                                note="Above the highest band the quote turns custom."
                              />
                            </div>

                            <div className="asp-price-grid">
                              <Field {...f} name="included_users" label="Included users" placeholder="5" />
                              <Field {...f} name="extra_user_cost" label="Extra user cost" suffix="₹/mo" placeholder="1999" />
                            </div>

                            <div className="asp-price-grid">
                              <Field {...f}
                                name="free_clients" label="Free clients" placeholder="0"
                                note="−1 means unlimited. Blank if this band has no client book."
                              />
                              {/* An unlimited book has no extras to charge for, so
                                  the field would only collect a number nothing
                                  would ever apply. */}
                              {Number(form.free_clients) === -1 ? (
                                <div className="asp-field">
                                  <label className="asp-label">Extra client cost</label>
                                  <div style={{ fontSize: 12.5, color: "#8b92a9", padding: "10px 0" }}>
                                    Not charged — this band includes unlimited clients.
                                  </div>
                                </div>
                              ) : (
                                <Field {...f} name="extra_client_cost" label="Extra client cost" suffix="₹/mo" placeholder="0" />
                              )}
                            </div>

                            <Field {...f} name="sort_order" label="Sort order" placeholder="3" note="Where this band sits in the list." />
                          </div>

                          {/* ── Discounts ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Discounts</div>
                            <div className="asp-price-grid">
                              <Field {...f} name="quarterly_discount_pct" label="Quarterly prepay" suffix="%" placeholder="5" />
                              <Field {...f} name="annual_discount_pct" label="Annual prepay" suffix="%" placeholder="20" />
                            </div>
                            <div className="asp-price-grid">
                              <Field {...f}
                                name="max_additional_discount_pct" label="Max additional" suffix="%" placeholder="20"
                                note="The ceiling a rep can give away on their own."
                              />
                              <Field {...f}
                                name="max_onboarding_discount_pct" label="Max onboarding" suffix="%" placeholder="100"
                                note="100 lets onboarding be waived entirely."
                              />
                            </div>
                            <div className="asp-price-grid">
                              <Field {...f} name="onboarding_charge" label="Onboarding charge" suffix="₹" placeholder="9999" />
                              <Field {...f} name="gst_percent" label="GST" suffix="%" placeholder="18" />
                            </div>
                          </div>

                          {/* ── Overage ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Overage</div>
                            <div className="asp-price-grid">
                              <Field {...f} name="overage_threshold_marks" label="Threshold" suffix="marks" placeholder="—" />
                              <Field {...f} name="overage_per_mark_yr" label="Overage rate" suffix="₹/mark/yr" placeholder="—" />
                            </div>
                            <div style={{ fontSize: 12, color: "#8b92a9", lineHeight: 1.5 }}>
                              Only the top band of a segment usually charges for overage — leave
                              these blank everywhere else.
                            </div>
                          </div>

                          {/* ── Plan & features ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Plan &amp; Features</div>

                            <div className="asp-field">
                              <label className="asp-label" htmlFor="plan_id">Linked plan</label>
                              <select
                                id="plan_id" className="form-control" value={form.plan_id}
                                onChange={(e) => { set({ plan_id: e.target.value }); setFeaturesTouched(false); }}
                              >
                                <option value="">None — this tier carries its own features</option>
                                {plans.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name || p.plan_name}
                                    {p.is_proposal_only ? " — band plan (not sold directly)" : ""}
                                  </option>
                                ))}
                              </select>
                              <div style={{ fontSize: 12, color: "#8b92a9", marginTop: 6, lineHeight: 1.5 }}>
                                Linked plan sets the base; edit features here to customize what this
                                tier's customers get.
                              </div>
                            </div>

                            {linkedPlan && (
                              <div style={{ background: "#f4f6fd", border: "1px solid #dbe1f5", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12.5, color: "#3b5bdb", lineHeight: 1.6 }}>
                                Started from the linked plan's features — change any of them and this
                                tier keeps your list, not the plan's.
                              </div>
                            )}

                            {featureLoading ? (
                              <div className="asp-feature-grid">
                                {Array.from({ length: 6 }).map((_, i) => (
                                  <div key={i} style={{ background: "#f8f9fe", border: "1.5px solid #e2e6f0", borderRadius: 12, padding: 14 }}>
                                    <span className="asp-skeleton" style={{ width: "60%", height: 14, marginBottom: 8, display: "block" }} />
                                    <span className="asp-skeleton" style={{ width: "90%", height: 11 }} />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="asp-feature-grid">
                                {[...masterFeatures]
                                  .sort((a, b) => (a.feature_order || 0) - (b.feature_order || 0))
                                  .map((item) => {
                                    const on = features.includes(item.id);
                                    return (
                                      <div
                                        key={item.id}
                                        className={`asp-feature-card ${on ? "asp-feature-checked" : ""}`}
                                      >
                                        <div className="asp-feature-card-top">
                                          <label className="asp-feature-check-label" htmlFor={`tier-feature-${item.id}`}>
                                            <input
                                              className="form-check-input" type="checkbox"
                                              id={`tier-feature-${item.id}`}
                                              checked={on}
                                              onChange={() => toggleFeature(item.id)}
                                            />
                                            <span className="asp-feature-name">{item.name}</span>
                                          </label>
                                        </div>
                                        <div className="asp-feature-desc">
                                          {item.description?.length > 60
                                            ? `${item.description.substring(0, 60)}…`
                                            : item.description}
                                        </div>
                                        <div className="asp-feature-selected-tick">✓</div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                            {errors.features && <div className="asp-error" style={{ marginTop: 12 }}>{errors.features}</div>}
                          </div>

                          {/* ── Active ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Availability</div>
                            <div className="asp-toggle-row">
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input" type="checkbox" role="switch"
                                  id="is_active" checked={form.is_active}
                                  onChange={(e) => set({ is_active: e.target.checked })}
                                />
                              </div>
                              <div className="asp-toggle-info">
                                <strong className={form.is_active ? "ok" : "no"}>
                                  {form.is_active ? "Active" : "Inactive"}
                                </strong>
                                <br />
                                <small>
                                  {form.is_active
                                    ? "Reps can quote this band."
                                    : "Hidden from the Package dropdown. Quotes already sent keep their prices."}
                                </small>
                              </div>
                            </div>
                          </div>

                        </div>
                        </fieldset>

                        <div className="asp-form-footer">
                          <Link to="/cms/rate-card" className="asp-btn asp-btn-cancel">
                            {isView ? "Back to Rate Card" : "Cancel"}
                          </Link>
                          {isView ? (
                            <Link
                              to={`/cms/rate-card/edit-tier/${id}?segment=${segment}`}
                              className="asp-btn asp-btn-submit"
                            >
                              ✏ Edit this tier
                            </Link>
                          ) : (
                            <button type="submit" className="asp-btn asp-btn-submit" disabled={submitting}>
                              {submitting
                                ? (isEdit ? "Updating…" : "Creating…")
                                : (isEdit ? "✓ Update Tier" : "+ Add Tier")}
                            </button>
                          )}
                        </div>
                      </form>

                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default AddEditTier;
